import React, { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts";
import rawHeatmap from "./jabil-from-25-07-01.json";

// ... (getRows and getDisplayName functions remain unchanged)
const getRows = () => {
  if (Array.isArray(rawHeatmap)) return rawHeatmap;
  if (Array.isArray(rawHeatmap?.data)) return rawHeatmap.data;
  return [];
};

const getDisplayName = (row = {}) => {
  const { user_details = {}, email } = row;
  const { firstname = "", lastname = "" } = user_details;

  const name = `${firstname} ${lastname}`.trim();
  return name || email || "Unnamed user";
};
// ...

const HeatMap = () => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [userCount, setUserCount] = useState(12);
  const [actionCount, setActionCount] = useState(14);

  const rows = getRows();

  const { users, actions, values, colorMap, maxValue, isEmpty, totalUsers, totalActions } =
    useMemo(() => {
      if (!rows.length) return { users: [], actions: [], values: [], colorMap: {}, maxValue: 0, isEmpty: true, totalUsers: 0, totalActions: 0 };

      const processedRows = rows.map((row) => {
        const total = Array.isArray(row.actions)
          ? row.actions.reduce(
            (sum, action) => sum + (Number(action?.count) || 0),
            0
          )
          : 0;

        return { ...row, total, label: getDisplayName(row) };
      });

      processedRows.sort((a, b) => b.total - a.total);

      const allUsers = processedRows;

      const actionTotals = new Map();
      allUsers.forEach((row) => {
        (row.actions || []).forEach(({ type, count }) => {
          if (!type) return;
          const numeric = Number(count) || 0;
          actionTotals.set(type, (actionTotals.get(type) || 0) + numeric);
        });
      });

      const allActions = Array.from(actionTotals.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name]) => name);

      const cappedUserCount = Math.max(
        1,
        Math.min(userCount, allUsers.length || 1)
      );
      const cappedActionCount = Math.max(
        1,
        Math.min(actionCount, allActions.length || 1)
      );

      const selectedUsers = allUsers.slice(0, cappedUserCount);
      const selectedActions = allActions.slice(0, cappedActionCount);

      const values = [];
      const colorMap = {};
      let maxVal = 0;

      selectedUsers.forEach((row, yIndex) => {
        const lookup = {};
        const colorLookup = {};
        (row.actions || []).forEach(({ type, count, color }) => {
          if (type) {
            lookup[type] = Number(count) || 0;
            colorLookup[type] = color || "#FF0000";
          }
        });

        selectedActions.forEach((actionName, xIndex) => {
          const value = lookup[actionName] || 0;
          const color = colorLookup[actionName] || "#FF0000";
          if (value > maxVal) maxVal = value;

          // Store color in map using coordinates as key
          const key = `${xIndex},${yIndex}`;
          colorMap[key] = color;

          // Use ECharts object format with itemStyle for individual colors
          values.push({
            value: [xIndex, yIndex, value],
            itemStyle: {
              color: color
            }
          });
        });
      });

      return {
        users: selectedUsers.map(({ label }) => label),
        actions: selectedActions,
        values,
        colorMap,
        maxValue: maxVal,
        isEmpty: false,
        totalUsers: allUsers.length,
        totalActions: allActions.length,
      };
    }, [rows, userCount, actionCount]);

  // Dynamic Height Calculation remains
  const chartHeight = useMemo(() => {
    const baseHeight = 180;
    const itemHeight = 35;
    return Math.max(320, baseHeight + users.length * itemHeight);
  }, [users]);

  const option = useMemo(() => {
    if (isEmpty) return null;

    return {
      tooltip: {
        position: "top",
        formatter: (params) => {
          const [xIndex, yIndex, value] = params.data.value;
          const userLabel = users[yIndex] || 'User not found';
          const actionLabel = actions[xIndex] || 'Action not found';

          return [
            `<strong>${userLabel}</strong>`,
            actionLabel,
            `${value} action${value === 1 ? "" : "s"}`,
          ].join("<br/>");
        },
        extraCssText: "text-align:left",
      },

      grid: {
        top: 100,
        right: 24,
        bottom: 40,
        left: 160,
        containLabel: false,
      },

      xAxis: {
        type: "category",
        position: "top",
        data: actions,
        splitArea: { show: true },
        axisLabel: {
          interval: 0,
          rotate: actions.length > 6 ? 30 : 0,
          formatter: (value) => {
            return value.length > 15 ? value.substring(0, 15) + '...' : value;
          }
        },
      },

      yAxis: {
        type: "category",
        data: users,
        splitArea: { show: true },
        axisLabel: {
          margin: 10,
        }
      },

      visualMap: {
        show: false,
        min: 0,
        max: Math.max(maxValue, 1),
        inRange: {
          color: ["#FFFFFF", "#FFFFFF"]  // Set to white so it doesn't interfere
        }
      },

      series: [
        {
          type: "heatmap",
          data: values,
          animation: false,
          label: {
            show: true,
            color: "#ffffff",
            fontWeight: 600,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 12,
              shadowColor: "rgba(0, 0, 0, 0.25)",
            },
          },
        },
      ],
    };
  }, [actions, users, values, maxValue, isEmpty]);


  // --- FINAL FIX: Deferred Initialization for DOM Sizing ---
  useEffect(() => {
    const chartDom = chartRef.current;

    // Cleanup any existing instance
    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    if (!chartDom) {
      return;
    }

    // Use setTimeout(0) to defer initialization until the DOM layout 
    // for the new chartHeight has been completed by the browser.
    const initializeChart = () => {
      // Double-check the DOM element and option before initializing
      if (!chartDom || !option) return;

      try {
        const chart = echarts.init(chartDom);
        chartInstance.current = chart;

        chart.setOption(option, true);

        // Re-apply window resize listener to the new instance
        const handleResize = () => {
          chart.resize();
        };
        window.addEventListener('resize', handleResize);
      } catch (error) {
        console.error("ECharts initialization failed:", error);
      }
    };

    const timer = setTimeout(initializeChart, 0);

    // Cleanup function runs on unmount OR before re-running the effect
    return () => {
      clearTimeout(timer);
      const chart = chartInstance.current;
      if (chart) {
        // Remove the resize listener before disposing
        const handleResize = () => { chart.resize(); };
        window.removeEventListener('resize', handleResize);

        chart.dispose();
      }
      chartInstance.current = null;
    };

    // Trigger on option change (data/actions/values) AND chartHeight change (user count)
  }, [option, chartHeight]);
  // --- END FINAL FIX ---

  return (
    <div className="heatmap-wrapper">
      <div className="heatmap-card">
        <div className="heatmap-header">
          <div>
            <p className="heatmap-eyebrow">Engagement signals</p>
            <h5>User activity heatmap</h5>
          </div>
          {!isEmpty && (
            <span className="heatmap-meta">
              Showing {users.length} of {totalUsers} users · {actions.length} of{" "}
              {totalActions} actions
            </span>
          )}
        </div>

        {isEmpty ? (
          <div className="heatmap-empty">
            No activity captured for the selected users yet.
          </div>
        ) : (
          <>
            <div className="heatmap-controls">
              <div className="heatmap-slider">
                <label htmlFor="userRange">
                  Users visible: <strong>{users.length}</strong> / {totalUsers}
                </label>
                <input
                  id="userRange"
                  type="range"
                  min={Math.min(5, totalUsers) > 1 ? 5 : 1}
                  max={Math.max(totalUsers, 1)}
                  value={Math.min(userCount, Math.max(totalUsers, 1))}
                  onChange={(event) => setUserCount(Number(event.target.value))}
                />
              </div>
              <div className="heatmap-slider">
                <label htmlFor="actionRange">
                  Actions visible: <strong>{actions.length}</strong> / {totalActions}
                </label>
                <input
                  id="actionRange"
                  type="range"
                  min={Math.min(5, totalActions) > 1 ? 5 : 1}
                  max={Math.max(totalActions, 1)}
                  value={Math.min(actionCount, Math.max(totalActions, 1))}
                  onChange={(event) => setActionCount(Number(event.target.value))}
                />
              </div>
            </div>
            <div className="heatmap-scroll-wrapper">
              <div
                ref={chartRef}
                className="heatmap-chart"
                // The height is correctly set here to be dynamic
                style={{ height: `${chartHeight}px`, minWidth: "900px" }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HeatMap;